import { Unwrap } from '../interfaces'

/**
 * invokes {@link fetch} using the supplied args and handles common response
 * issues we run into
 *
 * @see {@link https://developer.mozilla.org/docs/Web/API/fetch}
 */
export async function fetchAndParseJSON<T = any>(
  ...args: Parameters<typeof fetch>
): Promise<
  | FetchAndParseJSONResponse<{
      type: 'body'
      /**
       * the succesfully parsed json, coerced into {@link T}
       */
      body: T
    }>
  | FetchAndParseJSONResponse<{
      type: 'text'
      /**
       * text which caused the parse to throw an error whose message started
       * with `Unexpected token '<'`
       */
      text: string
    }>
  | FetchAndParseJSONResponse<{
      type: 'error'
      /**
       * an error instance thrown during text conversion or json parsing
       *
       * its message did not start with `Unexpected token '<'`
       */
      error: Error
    }>
  | FetchAndParseJSONResponse<{
      type: 'unknown'
      /**
       * the state of the text when a non-Error error was thrown
       */
      text: string | undefined
      /**
       * the thrown error is not an Error instance
       */
      error: unknown
    }>
> {
  let text
  const response = await fetch(...args)
  try {
    text = await response.text()
    return { type: 'body', response, body: JSON.parse(text) as T }
  } catch (err) {
    if (err instanceof Error) {
      if (text && err.message.startsWith("Unexpected token '<'"))
        return { type: 'text', response, text }
      return { type: 'error', response, error: err }
    }
    return { type: 'unknown', response, text, error: err }
  }
}

/**
 * uses {@link fetchAndParseJSON} but throws an error if the type isn't `body`
 *
 * @see {@link fetchAndParseJSON}
 * @see {@link https://developer.mozilla.org/docs/Web/API/fetch}
 */
export async function fetchAndParseJSONOrFail<T = any>(
  ...args: Parameters<typeof fetch>
) {
  const f = await fetchAndParseJSON<T>(...args)
  if (f.type === 'body') return f.body
  const e = (message: string | undefined) =>
    new Error(
      `fetchAndParseJSON failed (${f.response.status}: ${f.response.statusText}): ${message}`
    )
  if (f.type === 'error') throw e(f.error.message)
  else if (f.type === 'text') throw e(`text: ${f.text}`)
  else throw e(`text: ${f.text}. unknown: ${f.error}`)
}

type FetchAndParseJSONResponse<T extends { type: string }> = Unwrap<
  T & {
    /**
     * the response associated with the fetch
     */
    response: Response
  }
>
